import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Mail, Phone, MessageCircle, Facebook, Music2, Send, CheckCircle2, Loader2, CalendarDays,
} from "lucide-react";
import { Reveal, FadeIn } from "../components/Reveal";
import { BookingTrigger } from "../components/BookingDialog";
import { useLang } from "../LanguageContext";
import { CONTACT, IMAGES, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">{label}</span>
    {children}
  </label>
);

const ContactForm = () => {
  const { lang, s } = useLang();
  const f = s.contact.form;
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const serviceOptions = [...s.offers.map((o) => `${o.title} (${o.tagline}) — ${o.price}`), s.booking.unknown];

  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/contact`, { ...form, lang });
      setStatus("success");
      setForm(initialForm);
      toast.success(data?.email_sent ? s.toasts.contactOk : s.toasts.contactOkNoEmail);
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.data?.detail?.message || s.toasts.contactErr);
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="glass flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl p-10 text-center">
        <CheckCircle2 className="h-14 w-14 text-mint" />
        <h3 className="mt-6 font-serif text-3xl text-white">{f.successTitle}</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">{f.successText}</p>
        <button data-testid="contact-reset-button" onClick={() => setStatus("idle")} className="btn-ghost mt-8">
          {f.successReset}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="glass rounded-3xl p-7 sm:p-9">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={f.name}>
          <input required data-testid="contact-name-input" className="field" value={form.name} onChange={set("name")} placeholder={f.namePh} />
        </Field>
        <Field label={f.email}>
          <input required type="email" data-testid="contact-email-input" className="field" value={form.email} onChange={set("email")} placeholder={f.emailPh} />
        </Field>
        <Field label={f.phone}>
          <input type="tel" data-testid="contact-phone-input" className="field" value={form.phone} onChange={set("phone")} placeholder={f.phonePh} />
        </Field>
        <Field label={f.service}>
          <select data-testid="contact-service-select" className="field appearance-none" value={form.service} onChange={set("service")}>
            <option value="" className="bg-night">{f.servicePh}</option>
            {serviceOptions.map((opt) => <option key={opt} value={opt} className="bg-night">{opt}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-5">
        <Field label={f.message}>
          <textarea required minLength={5} rows={5} data-testid="contact-message-input" className="field resize-none" value={form.message} onChange={set("message")} placeholder={f.messagePh} />
        </Field>
      </div>
      <button type="submit" data-testid="contact-submit-button" disabled={status === "sending"} className="btn-gold mt-7 w-full disabled:cursor-wait disabled:opacity-60">
        {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {status === "sending" ? f.sending : f.submit}
      </button>
      <p className="mt-4 text-center text-[11px] text-white/40">{f.note}</p>
    </form>
  );
};

const Channel = ({ href, icon: Icon, label, value, testid, accent = "text-gold" }) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel="noopener noreferrer"
    data-testid={testid}
    className="group flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/30 hover:bg-white/[0.06]"
  >
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] ${accent}`}>
      <Icon className="h-[18px] w-[18px]" />
    </div>
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</p>
      <p className="mt-1 truncate text-sm font-medium text-white/85 group-hover:text-gold-light">{value}</p>
    </div>
  </a>
);

const Contact = () => {
  const { s } = useLang();
  const c = s.contact;
  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden pb-6 pt-40">
        <img src={IMAGES.veil} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/85 to-obsidian" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <FadeIn><p className="eyebrow">{c.eyebrow}</p></FadeIn>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[1.02] text-white sm:text-6xl">
            {c.titleA}<br /><span className="italic text-halo">{c.titleB}</span>
          </h1>
          <FadeIn delay={0.45}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/65">{c.intro}</p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-6xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
        <Reveal><ContactForm /></Reveal>

        <Reveal delay={0.12}>
          <div className="flex h-full flex-col gap-4">
            <BookingTrigger testid="contact-booking-open-button" className="btn-gold w-full">
              <CalendarDays className="h-4 w-4" /> {c.bookCta}
            </BookingTrigger>
            <Channel
              testid="contact-channel-email"
              href={`mailto:${CONTACT.email}`} icon={Mail} label={c.channels.email}
              value={CONTACT.email}
            />
            <Channel
              testid="contact-channel-whatsapp"
              href={waLink(s.wa.defaultMsg)} icon={MessageCircle} label={c.channels.whatsapp}
              value={CONTACT.phoneDisplay} accent="text-mint"
            />
            <Channel
              testid="contact-channel-phone"
              href={`tel:${CONTACT.phoneRaw}`} icon={Phone} label={c.channels.phone}
              value={CONTACT.phoneDisplay}
            />
            <Channel
              testid="contact-channel-facebook"
              href={CONTACT.facebookHub} icon={Facebook} label={c.channels.facebook}
              value="Porte de Lumière" accent="text-sky2"
            />
            <Channel
              testid="contact-channel-tiktok"
              href={CONTACT.tiktok} icon={Music2} label={c.channels.tiktok}
              value="Porte de Lumière" accent="text-iris"
            />
            <motion.p
              className="mt-2 border-l-2 border-gold/40 pl-5 font-serif text-lg italic text-gold-light/90"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              {c.quote}
            </motion.p>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Contact;
